import {
	FONT_SCALES,
	LETTER_SPACINGS,
	LINE_HEIGHTS,
	WORD_SPACINGS,
	type Prefs,
} from './prefs';

const HOST = 'nagish-marketbiz-toolbar';
const ALL = `:root:root body :not(${ HOST })`;
const LINKS = `:root:root body :is(a,a *):not(${ HOST })`;
const HEADINGS = `:root:root body :is(h1,h2,h3,h4,h5,h6,[role=heading]):not(${ HOST })`;
const ICONS =
	':not(i):not([class*=icon]):not([class*=fa-]):not(.fa):not(.fas):not(.far):not(.fab):not(.dashicons)';

const arrow =
	"url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='48' height='48'%3E%3Cpath d='M6 3v34l9-8 6 14 7-3-6-13h12z' fill='%23000' stroke='%23fff' stroke-width='3' stroke-linejoin='round'/%3E%3C/svg%3E\") 6 3, auto";
const hand =
	"url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='48' height='48'%3E%3Cpath d='M16 4a4 4 0 0 1 8 0v14h2a4 4 0 0 1 6 0 4 4 0 0 1 6 2 4 4 0 0 1 5 4v12c0 7-5 10-12 10h-4c-6 0-9-3-12-8L7 29c-1-3 2-5 5-3l4 4z' fill='%23000' stroke='%23fff' stroke-width='3' stroke-linejoin='round'/%3E%3C/svg%3E\") 20 4, pointer";

/**
 * The page-wide stylesheet for the active preferences. Pure; the toolbar's own DOM is excluded everywhere.
 * @param p
 * @param fontUrl
 */
export const buildPageCss = ( p: Prefs, fontUrl: string ): string => {
	const css: string[] = [];
	const dyslexia = p.dyslexiaFont;
	if ( dyslexia && fontUrl ) {
		css.push(
			`@font-face{font-family:"Nagish Dyslexic";src:url("${ fontUrl.replace( /["\\]/g, '' ) }");font-display:swap}`
		);
	}
	if ( p.lineHeight ) {
		css.push(
			`${ ALL }{line-height:${ LINE_HEIGHTS[ p.lineHeight ] }!important}`
		);
	}
	const letter =
		LETTER_SPACINGS[ p.letterSpacing ] || ( dyslexia ? 0.05 : 0 );
	const word = WORD_SPACINGS[ p.wordSpacing ] || ( dyslexia ? 0.1 : 0 );
	if ( letter ) {
		css.push( `${ ALL }{letter-spacing:${ letter }em!important}` );
	}
	if ( word ) {
		css.push( `${ ALL }{word-spacing:${ word }em!important}` );
	}
	if ( dyslexia ) {
		const face = fontUrl ? '"Nagish Dyslexic",' : '';
		css.push(
			`${ ALL }${ ICONS }{font-family:${ face }OpenDyslexic,Lexend,"Atkinson Hyperlegible","Comic Sans MS",Verdana,sans-serif!important}`
		);
	} else if ( p.readableFont ) {
		css.push(
			`${ ALL }${ ICONS }{font-family:Arial,"Segoe UI",Tahoma,Helvetica,sans-serif!important}`
		);
	}
	if ( p.contrast === 'high' ) {
		css.push(
			`:root:root,:root:root body,${ ALL }{background:#000!important;color:#fff!important;border-color:#fff!important;text-shadow:none!important;box-shadow:none!important}`,
			`${ LINKS }{color:#ff0!important;text-decoration:underline!important}`,
			`:root:root body :is(button,input,select,textarea):not(${ HOST }){background:#000!important;color:#fff!important;border:1px solid #fff!important}`
		);
	} else if ( p.contrast === 'inverted' ) {
		css.push(
			':root:root{filter:invert(1) hue-rotate(180deg)!important}',
			`:root:root body :is(img,picture,video,canvas):not(${ HOST }){filter:invert(1) hue-rotate(180deg)!important}`
		);
	} else if ( p.contrast === 'grayscale' ) {
		css.push( ':root:root{filter:grayscale(1)!important}' );
	}
	if ( p.highlightLinks ) {
		css.push(
			`${ LINKS }{background:#ffeb3b!important;color:#000!important;text-decoration:underline!important;outline:2px solid #000!important}`
		);
	}
	if ( p.highlightHeadings ) {
		css.push(
			`${ HEADINGS }{background:#e3f0ff!important;color:#000!important;outline:3px solid #0b3d91!important;outline-offset:2px!important}`
		);
	}
	if ( p.bigCursor ) {
		css.push(
			`:root:root,:root:root body,${ ALL }{cursor:${ arrow }!important}`,
			`:root:root body :is(a,button,[role=button],input,select,summary,label,a *):not(${ HOST }){cursor:${ hand }!important}`
		);
	}
	if ( p.stopAnimations ) {
		css.push(
			`*,*::before,*::after{animation-duration:.001ms!important;animation-iteration-count:1!important;animation-delay:0s!important;transition-duration:.001ms!important;transition-delay:0s!important;scroll-behavior:auto!important}`
		);
	}
	if ( p.hideImages ) {
		css.push(
			`${ ALL }{background-image:none!important}`,
			`:root:root body :is(img,picture,video,[role=img]):not(${ HOST }){visibility:hidden!important}`
		);
	}
	if ( p.keyboardHints ) {
		css.push(
			`:root:root body :focus-visible:not(${ HOST }){outline:3px solid #ff6d00!important;outline-offset:3px!important}`
		);
	}
	return css.join( '\n' );
};

export const createPageSheet = (): ( ( css: string ) => void ) => {
	if (
		'adoptedStyleSheets' in document &&
		typeof CSSStyleSheet !== 'undefined' &&
		'replaceSync' in CSSStyleSheet.prototype
	) {
		const sheet = new CSSStyleSheet();
		document.adoptedStyleSheets = [ ...document.adoptedStyleSheets, sheet ];
		return ( css ) => sheet.replaceSync( css );
	}
	const style = document.createElement( 'style' );
	style.id = 'nagish-page-styles';
	document.head.appendChild( style );
	return ( css ) => {
		style.textContent = css;
	};
};

const SKIP = new Set( [
	'SCRIPT',
	'STYLE',
	'LINK',
	'META',
	'NOSCRIPT',
	'TEMPLATE',
	'HEAD',
	'TITLE',
	'BR',
	'WBR',
] );

/** Scales the rendered text size of the page by setting absolute pixel sizes (restored on reset). */
export const createFontScaler = () => {
	const saved = new Map< HTMLElement, { value: string; priority: string } >();
	let level = 0;
	let timer = 0;
	const observer = new MutationObserver( () => {
		window.clearTimeout( timer );
		timer = window.setTimeout( () => apply( level ), 250 );
	} );

	const restore = () => {
		for ( const [ el, orig ] of saved ) {
			if ( orig.value ) {
				el.style.setProperty( 'font-size', orig.value, orig.priority );
			} else {
				el.style.removeProperty( 'font-size' );
			}
		}
		saved.clear();
	};

	const apply = ( next: number ) => {
		level = next;
		observer.disconnect();
		restore();
		const scale = FONT_SCALES[ level ] ?? 1;
		if ( scale !== 1 ) {
			const targets = [
				document.documentElement,
				...document.body.querySelectorAll< HTMLElement >( '*' ),
			].filter(
				( el ): el is HTMLElement =>
					el instanceof HTMLElement &&
					! SKIP.has( el.tagName ) &&
					el.tagName.toLowerCase() !== HOST
			);
			const sizes = targets.map( ( el ) =>
				parseFloat( getComputedStyle( el ).fontSize )
			);
			targets.forEach( ( el, i ) => {
				const px = sizes[ i ];
				if ( ! px ) {
					return;
				}
				saved.set( el, {
					value: el.style.getPropertyValue( 'font-size' ),
					priority: el.style.getPropertyPriority( 'font-size' ),
				} );
				el.style.setProperty(
					'font-size',
					`${ Math.round( px * scale * 100 ) / 100 }px`,
					'important'
				);
			} );
			observer.observe( document.body, {
				childList: true,
				subtree: true,
			} );
		}
	};
	return apply;
};
